if(!customElements.get('cart-drawer')){
    class CartDrawer extends HTMLElement {
        constructor(){
            super();

            this.sectionId = this.dataset.id;

            // all cart mutations (quantity changes, removals, refreshes) run through
            // this chain one at a time - concurrent /cart/change requests can be
            // rejected by Shopify or resolve out of order
            this.cartMutationChain = Promise.resolve();
            this.loadingCount = 0;

            this.init();
        }

        queueMutation(task){
            var run = this.cartMutationChain.catch(() => {}).then(task);
            this.cartMutationChain = run.catch(() => {});
            return run;
        }

        // data-loading reflects the whole chain, not one task - counted, so an early
        // task finishing can't lift the interaction gate while later tasks are queued
        setLoading(isLoading){
            this.loadingCount += isLoading? 1 : -1;
            if(this.loadingCount < 0) this.loadingCount = 0;
            this.setAttribute('data-loading', (this.loadingCount > 0)? 'true' : 'false');
        }

        // abort a request that hangs instead of wedging the mutation chain forever
        requestSignal(){
            return (typeof AbortSignal !== 'undefined' && AbortSignal.timeout)? AbortSignal.timeout(15000) : undefined;
        }

        init(){
            // backdrop click - close when clicking outside the panel
            this.addEventListener('click', (e) => {
                if(e.target === this){
                    this.closeSidebar();
                }
            });

            // escape key - but when the quick-add variant popup is open above the
            // drawer, Escape belongs to the popup: close it here (via its close
            // button, so the owning product-form runs its own close path) instead
            // of the drawer. Handling it at document level covers focus sitting
            // on body after the popup's async open, where the popup's own
            // focus-dependent listeners never hear the key.
            document.addEventListener('keydown', (e) => {
                if(e.key === 'Escape' && this.getAttribute('data-hidden') == 'false'){
                    var section = this.closest('.shopify-section');
                    var openPopup = section && section.querySelector('variant-popup[data-hidden="false"]');
                    if(openPopup){
                        var btnClose = openPopup.querySelector('[data-type="close"]');
                        if(btnClose) btnClose.click();
                        return;
                    }
                    this.closeSidebar();
                }
            });

            // an item was added to the cart somewhere in the theme (product page,
            // quick-add, this drawer's recommended products). If the drawer is
            // open, close it so the confirmation popup is visible and refresh in
            // the background; when closed, openSidebar refreshes on open anyway.
            document.addEventListener('cart:item-added', () => {
                if(this.getAttribute('data-hidden') == 'false'){
                    this.closeSidebar();
                    this.refreshFromServer();
                }
            });

            this.bindCartEvents();
        }

        // (re)bind the events on the cart contents. Called on init and after the
        // item list is re-rendered, so the same listeners are attached to fresh nodes.
        bindCartEvents(){
            this.btnClose = this.querySelector('.btn-close');
            if(this.btnClose && !this.btnClose.dataset.bound){
                this.btnClose.dataset.bound = 'true';
                this.btnClose.addEventListener('click', () => this.closeSidebar());
            }

            this.cartItems = this.querySelector('.sidebar__cart-items');
            this.subTotals = this.querySelector('.sidebar__cart-subtotal-value');
            this.errorMsgs = this.querySelectorAll('.sidebar__cart-item-errors');

            this.btnItemRemoves = this.querySelectorAll('.btn-item-remove');
            this.btnItemRemoves.forEach(item => {
                item.addEventListener('click', this.clickItemRemove.bind(this));
            });

            this.quantitySelectors = this.querySelectorAll('quantity-selector');
            this.quantitySelectors.forEach(item => {
                var btnRemoveQuantity = item.querySelector('[data-action="remove"]');
                var btnAddQuantity = item.querySelector('[data-action="add"]');
                var input = item.querySelector('.input-quantity');

                // disable the minus button when the quantity is already 1
                this.updateRemoveState(item);

                // coalesce rapid +/- clicks and typing into a single request:
                // the displayed value updates instantly (handled by the shared
                // quantity-selector element), the request fires once the user pauses.
                // input.debounceTimer doubles as the "edit pending" flag.
                var queueUpdate = () => {
                    this.updateRemoveState(item);
                    clearTimeout(input.debounceTimer);
                    input.debounceTimer = setTimeout(() => {
                        input.debounceTimer = null;
                        this.updateQuantity({ currentTarget: input });
                    }, 400);
                };

                var handleRemove = () => {
                    if(btnRemoveQuantity.getAttribute('data-disabled') === 'true') return;
                    queueUpdate();
                };
                btnRemoveQuantity.addEventListener('click', handleRemove);
                btnRemoveQuantity.addEventListener('keydown', e => {
                    if(e.key === 'Enter'){
                        handleRemove();
                    }
                });
                btnAddQuantity.addEventListener('click', queueUpdate);
                btnAddQuantity.addEventListener('keydown', e => {
                    if(e.key === 'Enter'){
                        queueUpdate();
                    }
                });

                input.addEventListener('input', queueUpdate);

                // restore the last committed value if the field is left empty
                input.addEventListener('change', () => {
                    if(input.value.trim() === '' || isNaN(parseInt(input.value))){
                        input.value = input.getAttribute('value');
                    }
                });
            });
        }

        // enable/disable the minus button based on the current quantity
        updateRemoveState(quantitySelector){
            var input = quantitySelector.querySelector('.input-quantity');
            var btnRemove = quantitySelector.querySelector('[data-action="remove"]');
            if(!input || !btnRemove) return;

            if(parseInt(input.value) <= 1){
                btnRemove.setAttribute('data-disabled', 'true');
                btnRemove.setAttribute('aria-disabled', 'true');
                btnRemove.setAttribute('tabindex', '-1');
            }else{
                btnRemove.removeAttribute('data-disabled');
                btnRemove.setAttribute('aria-disabled', 'false');
                btnRemove.setAttribute('tabindex', '0');
            }
        }

        // commit any debounced quantity edits that haven't been sent yet, so a
        // removal or full re-render can't discard them. Edits inside exceptItem
        // are dropped instead - that line is about to be removed anyway.
        flushPendingQuantityUpdates(exceptItem){
            this.querySelectorAll('.input-quantity').forEach(input => {
                if(!input.debounceTimer) return;
                clearTimeout(input.debounceTimer);
                input.debounceTimer = null;
                if(exceptItem && exceptItem.contains(input)) return;
                this.updateQuantity({ currentTarget: input });
            });
        }

        refresh(){
            this.bindCartEvents();
        }

        openSidebar(){
            this.openPanel();

            // the cart may have changed since the drawer was rendered (e.g. an item
            // was added from a product page) - re-fetch so it always shows the live cart
            this.refreshFromServer();
        }

        openPanel(){
            this.previouslyFocusedElement = document.activeElement;

            document.querySelector('html').style.overflow = 'hidden';
            this.setAttribute('data-hidden', 'false');

            if(this.btnClose){
                setTimeout(() => {
                    if(this.btnClose) this.btnClose.focus();
                }, 600);
            }
        }

        closeSidebar(){
            this.setAttribute('data-hidden', 'true');
            document.querySelector('html').style.overflow = 'initial';

            if(this.previouslyFocusedElement){
                this.previouslyFocusedElement.focus();
            }
        }

        updateQuantity(e){
            var btn = e.currentTarget;
            var input = btn.closest('.quantity-wrapper').querySelector('input[name="quantity"]');
            var quantity = input.value;
            var itemKey = input.dataset.key;
            var itemLine = input.dataset.idx;
            var itemId = input.dataset.id;

            // ignore empty values while typing - the change listener restores them on blur
            if(quantity.trim() === '' || isNaN(parseInt(quantity))) return;

            var maxQuantity = parseInt(input.dataset.max);

            // correct values below the minimum to 1 (unless the item is out of stock;
            // removing an item goes through the remove link instead)
            if(parseInt(quantity) < 1){
                if(!isNaN(maxQuantity) && maxQuantity < 1) return;
                quantity = '1';
                input.value = 1;
                input.setAttribute('value', 1);
            }

            // clamp to the available inventory when the variant is stock-tracked
            if(!isNaN(maxQuantity) && maxQuantity > 0 && parseInt(quantity) > maxQuantity){
                quantity = `${maxQuantity}`;
                input.value = maxQuantity;
                input.setAttribute('value', maxQuantity);

                var maxMsg = (window.smallText && window.smallText.maxQuantityText)? window.smallText.maxQuantityText.replace('[QTY]', maxQuantity) : `Only ${maxQuantity} in stock.`;
                var errorWrapper = input.closest('.sidebar__cart-item').querySelector('.sidebar__cart-item-errors');
                if(errorWrapper){
                    errorWrapper.querySelector('.error').innerHTML = maxMsg;
                    errorWrapper.setAttribute('data-hidden', 'false');
                    errorWrapper.dataset.info = 'true';
                }
            }

            // keep the minus button state in sync with the new value
            this.updateRemoveState(btn.closest('quantity-selector'));

            this.setItemSpinner(itemKey, itemLine, true);

            // capture the validated values now - the task may run later, after the
            // input was edited again or the list re-rendered. The line item key stays
            // valid across re-renders and removals, unlike positional line numbers.
            var requestQuantity = `${quantity}`;
            this.queueMutation(() => {
                // the row may have been re-rendered while this task waited - read the
                // live line/key when the input is still attached
                var line = (input.isConnected && input.dataset.idx)? input.dataset.idx : itemLine;
                var key = (input.isConnected && input.dataset.key)? input.dataset.key : itemKey;

                var formData = new FormData();
                // address by LINE first: this store's cart app regenerates line item
                // keys after every change, so a stored key is dead by the second
                // request ("no valid id or line parameter"). Line numbers stay valid -
                // the drawer fully re-renders (fresh data-idx) after any removal.
                if(line){
                    formData.append("line", line);
                }else if(key){
                    formData.append("id", key);
                }else{
                    formData.append("id", itemId);
                }
                formData.append("quantity", requestQuantity);
                formData.append("sections_url", window.location.pathname);
                formData.append("sections", [this.sectionId, 'header-cart-icon']);
                return this.submitToCart(formData, key, line);
            });
        }

        // selector for one cart line - by line number first (item keys rotate on
        // every change on this store, so a stored key may already be stale)
        itemSelector(key, line){
            return line? `.sidebar__cart-item[data-idx="${line}"]` : `.sidebar__cart-item[data-key="${key}"]`;
        }

        setItemSpinner(key, line, isLoading){
            var spinner = this.querySelector(`${this.itemSelector(key, line)} .heading .spinner`);
            if(spinner) spinner.setAttribute('data-loading-status', isLoading? 'true' : 'false');
        }

        submitToCart(formData, key, line){
            const config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            config.body = formData;
            config.signal = this.requestSignal();

            return fetch(`${routes.cart_change_url}`, config)
            .then(res => res.json())
            .then(res => {
                // remove all error messages (informational ones, e.g. the inventory
                // clamp notice, survive one response so the shopper can read them)
                this.errorMsgs.forEach(item => {
                    if(item.dataset.info == 'true'){
                        delete item.dataset.info;
                        return;
                    }
                    item.setAttribute('data-hidden', 'true');
                    item.querySelector('.error').innerHTML = '';
                });

                var currentItem = this.querySelector(this.itemSelector(key, line));

                if(res.status){
                    if(!currentItem){
                        // the row this edit targeted no longer matches the DOM -
                        // the drawer is stale, resync it with the live cart
                        this.refreshFromServer();
                    }
                    if(currentItem){
                        currentItem.querySelector('.sidebar__cart-item-errors .error').innerHTML = `${res.message}`;
                        currentItem.querySelector('.sidebar__cart-item-errors').setAttribute('data-hidden', 'false');

                        // revert the optimistic quantity change
                        var currentQuantitySelector = currentItem.querySelector('quantity-selector');
                        if(currentQuantitySelector){
                            currentQuantitySelector.removeQuantity();
                            this.updateRemoveState(currentQuantitySelector);
                        }
                    }

                }else if(res.token){
                    const sectionHTML = new DOMParser().parseFromString(res.sections[this.sectionId], 'text/html');

                    // update subtotal
                    if(this.subTotals && sectionHTML.querySelector('.sidebar__cart-subtotal-value')){
                        this.subTotals.innerHTML = sectionHTML.querySelector('.sidebar__cart-subtotal-value').innerHTML;
                    }

                    // update heading count + item total
                    if(sectionHTML.querySelector('cart-drawer')){
                        this.setAttribute('data-num-items', sectionHTML.querySelector('cart-drawer').dataset.numItems);
                    }
                    if(this.querySelector('.sidebar__cart-count') && sectionHTML.querySelector('.sidebar__cart-count')){
                        this.querySelector('.sidebar__cart-count').innerHTML = sectionHTML.querySelector('.sidebar__cart-count').innerHTML;
                    }

                    // update the free shipping bar
                    const currentBar = this.querySelector('.sidebar__cart-shipping-bar');
                    const newBar = sectionHTML.querySelector('.sidebar__cart-shipping-bar');
                    if(currentBar && newBar){
                        currentBar.replaceWith(newBar);
                    }

                    // update this item's price + committed input value, matched by key
                    // so it targets the right line even after other lines were removed
                    const cartItem = sectionHTML.querySelector(this.itemSelector(key, line));
                    if(cartItem && currentItem){
                        // the change regenerated this line's item key - sync the fresh
                        // one onto the live row so key-based fallbacks stay usable
                        if(cartItem.dataset.key){
                            currentItem.dataset.key = cartItem.dataset.key;
                            var liveInput = currentItem.querySelector('.input-quantity');
                            if(liveInput) liveInput.dataset.key = cartItem.dataset.key;
                        }
                        var currentPrice = currentItem.querySelector('.sidebar__cart-item-price');
                        var newPrice = cartItem.querySelector('.sidebar__cart-item-price');
                        if(currentPrice && newPrice) currentPrice.innerHTML = newPrice.innerHTML;

                        var currentInput = currentItem.querySelector('.input-quantity');
                        var newInput = cartItem.querySelector('.input-quantity');
                        if(currentInput && newInput){
                            currentInput.value = newInput.value;
                            currentInput.setAttribute('value', newInput.value);
                        }

                        var currentSelector = currentItem.querySelector('quantity-selector');
                        if(currentSelector) this.updateRemoveState(currentSelector);
                    }

                    if(res.sections['header-cart-icon']){
                        this.updateCartCount(res.sections['header-cart-icon']);
                    }
                }

                this.setItemSpinner(key, line, false);
            })
            .catch(() => {
                // network failure/timeout - stop the spinner so the item is not stuck loading
                this.setItemSpinner(key, line, false);
            });
        }

        clickItemRemove(e){
            e.preventDefault();

            var removedItem = e.currentTarget.closest('.sidebar__cart-item');
            var itemKey = removedItem.dataset.key;
            var itemLine = removedItem.dataset.idx;

            // send any not-yet-sent quantity edits first (dropping the one for the
            // line being removed - that line is about to disappear anyway)
            this.flushPendingQuantityUpdates(removedItem);

            this.setLoading(true);

            this.queueMutation(() => this.removeItem(itemKey, itemLine));
        }

        removeItem(key, line){
            const config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            config.signal = this.requestSignal();

            var formData = new FormData();
            // line first - item keys rotate on every change on this store
            if(line){
                formData.append("line", line);
            }else{
                formData.append("id", key);
            }
            formData.append("quantity", '0');
            formData.append("sections_url", window.location.pathname);
            formData.append("sections", [this.sectionId, 'header-cart-icon']);
            config.body = formData;

            return fetch(`${routes.cart_change_url}`, config)
            .then(res => res.json())
            .then(res => {
                if(res.sections && res.sections[this.sectionId]){
                    this.renderDrawerSection(res.sections[this.sectionId]);
                }
                if(res.sections && res.sections['header-cart-icon']){
                    this.updateCartCount(res.sections['header-cart-icon']);
                }
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                this.setLoading(false);
            });
        }

        // re-fetch the whole drawer so it reflects the live cart (called on open and
        // available for other scripts after an item is added to the cart elsewhere)
        refreshFromServer(openAfter = false){
            // commit pending quantity edits first so the re-render can't discard them
            this.flushPendingQuantityUpdates();

            this.setLoading(true);

            // queued so a full re-render can't land in the middle of a pending
            // quantity change or removal
            this.queueMutation(() =>
                fetch(`${window.location.pathname}?sections=${this.sectionId},header-cart-icon`, { signal: this.requestSignal() })
                .then(res => res.json())
                .then(res => {
                    if(res[this.sectionId]){
                        this.renderDrawerSection(res[this.sectionId]);
                    }
                    if(res['header-cart-icon']){
                        this.updateCartCount(res['header-cart-icon']);
                    }
                    if(openAfter) this.openPanel();
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.setLoading(false);
                })
            );
        }

        renderDrawerSection(sectionText){
            const drawerWrapper = new DOMParser().parseFromString(sectionText, 'text/html').querySelector('cart-drawer');
            if(!drawerWrapper) return;

            this.setAttribute('data-num-items', drawerWrapper.dataset.numItems);

            // keep the live recommendations carousel across the wholesale swap so
            // the Swiper instance (and the shopper's scroll position) survive
            const liveCarousel = this.querySelector('cart-recommended-products');

            this.querySelector('.sidebar__cart-content-wrapper').innerHTML = drawerWrapper.querySelector('.sidebar__cart-content-wrapper').innerHTML;

            if(liveCarousel){
                const freshCarousel = this.querySelector('cart-recommended-products');
                if(freshCarousel) freshCarousel.replaceWith(liveCarousel);
            }

            this.refresh();
        }

        updateCartCount(sectionText){
            const countSection = new DOMParser().parseFromString(sectionText, 'text/html').querySelector('#shopify-section-header-cart-icon');
            const cartCount = document.querySelector('custom-header .nav-item .cart-count');

            if(countSection && cartCount){
                var count = countSection.textContent.trim();
                cartCount.innerHTML = count;
                cartCount.setAttribute('data-hidden', (parseInt(count) > 0)? 'false' : 'true');
            }
        }
    }

    customElements.define('cart-drawer', CartDrawer);
}
